import React, { useEffect, useState } from "react";
import { MapPin, GraduationCap, Hospital, Train, ShoppingBag } from "lucide-react";

/**
 * v2.70 Phase 4 — NearbyPlaces
 * Uses OpenStreetMap Overpass API (free, no key needed) to fetch schools,
 * hospitals, metros, and supermarkets within a 2 km radius of the property.
 * Only renders when lat/lng are present.
 */
const OVERPASS = "https://overpass-api.de/api/interpreter";

const CATEGORIES = [
  { key: "school",      label: "Schools",       Icon: GraduationCap, color: "#0EA5E9", query: '["amenity"~"^(school|college|university)$"]' },
  { key: "hospital",    label: "Hospitals",     Icon: Hospital,      color: "#DC2626", query: '["amenity"~"^(hospital|clinic|doctors)$"]' },
  { key: "metro",       label: "Metro/Transit", Icon: Train,         color: "#0055FF", query: '["railway"~"^(station|subway_entrance)$"]' },
  { key: "supermarket", label: "Shopping",      Icon: ShoppingBag,   color: "#92400E", query: '["shop"~"^(supermarket|mall|convenience)$"]' },
];

const RADIUS_M = 2000;

export default function NearbyPlaces({ latitude, longitude }) {
  const [byCat, setByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!latitude || !longitude) { setLoading(false); return; }
    const lat = Number(latitude), lng = Number(longitude);
    if (!isFinite(lat) || !isFinite(lng)) { setLoading(false); return; }
    const queries = CATEGORIES.map(({ key, query }) =>
      `node(around:${RADIUS_M},${lat},${lng})${query};out 6;`
    ).join("\n");
    const body = `[out:json][timeout:10];\n(${queries});`;
    setLoading(true);
    fetch(OVERPASS, {
      method: "POST",
      body: `data=${encodeURIComponent(body)}`,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("json")) throw new Error("non-json");
        return r.json();
      })
      .then((data) => {
        const grouped = {};
        for (const cat of CATEGORIES) grouped[cat.key] = [];
        for (const el of (data.elements || [])) {
          const tags = el.tags || {};
          const name = tags.name || tags["name:en"] || (tags.amenity ? tags.amenity.replace(/_/g, " ") : "Unnamed");
          const distM = haversineMeters(lat, lng, el.lat, el.lon);
          // Bucket — first cat that matches the tags
          let bucket = null;
          if (tags.amenity && ["school", "college", "university"].includes(tags.amenity)) bucket = "school";
          else if (tags.amenity && ["hospital", "clinic", "doctors"].includes(tags.amenity)) bucket = "hospital";
          else if (tags.railway && ["station", "subway_entrance"].includes(tags.railway)) bucket = "metro";
          else if (tags.shop && ["supermarket", "mall", "convenience"].includes(tags.shop)) bucket = "supermarket";
          if (bucket) grouped[bucket].push({ id: el.id, name, distM: Math.round(distM) });
        }
        // Keep top 4 per cat, sorted by distance
        for (const k of Object.keys(grouped)) {
          grouped[k] = grouped[k].sort((a, b) => a.distM - b.distM).slice(0, 4);
        }
        setByCat(grouped);
      })
      .catch((e) => setErr(e.message || "Couldn't load nearby places"))
      .finally(() => setLoading(false));
  }, [latitude, longitude]);

  if (!latitude || !longitude) return null;

  const totalCount = Object.values(byCat).reduce((s, a) => s + a.length, 0);
  // Hide entirely on fetch errors or zero results — Overpass is flaky on free tier;
  // we don't want a scary error block disrupting the property page.
  if (!loading && (err || totalCount === 0)) return null;

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="nearby-places">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-md bg-[#92400E]/10 text-[#92400E] flex items-center justify-center">
          <MapPin size={15} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-base text-[#09090B]">Nearby Places</h3>
          <p className="text-[11px] text-[#71717A]">Within 2 km · powered by OpenStreetMap</p>
        </div>
      </div>

      {loading ? (
        <div className="text-[12px] text-[#A1A1AA] py-4">Locating nearby amenities…</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {CATEGORIES.map(({ key, label, Icon, color }) => {
            const items = byCat[key] || [];
            if (items.length === 0) return null;
            return (
              <div key={key} className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3" data-testid={`nearby-${key}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon size={12} style={{ color }} />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-[#52525B]">{label}</span>
                  <span className="text-[10px] text-[#A1A1AA] ml-auto">{items.length} found</span>
                </div>
                <ul className="space-y-1">
                  {items.map((it) => (
                    <li key={it.id} className="text-[12px] flex items-baseline justify-between gap-2">
                      <span className="text-[#09090B] truncate">{it.name}</span>
                      <span className="font-mono-data text-[10.5px] text-[#52525B] shrink-0">{it.distM < 1000 ? `${it.distM} m` : `${(it.distM / 1000).toFixed(1)} km`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
